import React from 'react';
import { Paper, Stack, Typography, Box, IconButton, Tooltip, Avatar, AvatarGroup, Chip } from "@mui/material";
import { formatName, stringAvatar } from "../../Helpers/TextStyling";

const PRIORITY_MAP = {
    high: { color: '#ef4444', label: 'URGENT', bg: '#fee2e2', icon: 'icon-fire' },
    medium: { color: '#f59e0b', label: 'IMPORTANT', bg: '#fef3c7', icon: 'icon-star' },
    low: { color: '#10b981', label: 'INFO', bg: '#dcfce7', icon: 'icon-info' }
};

function NoteCard({ note, onEdit, onDelete, attendees, currentUserId }) {
    const config = PRIORITY_MAP[note.priority] || PRIORITY_MAP.low;

    return (
        <Paper
            elevation={0}
            sx={{
                p: '2px', // Thin border wrap for the gradient
                mb: 2,
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${config.color} 0%, transparent 40%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px -10px rgba(0,0,0,0.15)',
                }
            }}
        >
            <Box sx={{
                p: 2,
                bgcolor: '#fff',
                borderRadius: '18px', // Slightly smaller than paper to show gradient border
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* TOP ROW: Priority Badge & Actions */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Chip
                        label={config.label}
                        size="small"
                        sx={{
                            bgcolor: config.bg,
                            color: config.color,
                            fontWeight: 900,
                            fontSize: '9px',
                            height: '20px',
                            border: `1px solid ${config.color}44`,
                            '& .MuiChip-label': { px: 1 }
                        }}
                    />

                    <Stack direction="row" spacing={0}>
                        {note.private && (
                            <Tooltip title="Private Note">
                                <IconButton size="small" sx={{ color: '#94a3b8' }}>
                                    <i className="icon-lock" style={{ fontSize: '14px' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton size="small" onClick={() => onEdit(note)} sx={{ color: '#64748b', '&:hover': { color: '#0ea5e9' } }}>
                            <i className="icon-pencil" style={{ fontSize: '14px' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(note)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                            <i className="icon-trash" style={{ fontSize: '14px' }} />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* MIDDLE ROW: The Note Content */}
                <Box sx={{ flexGrow: 1, mb: 2, pl: 0.5 }}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '0.92rem',
                            lineHeight: 1.6,
                            color: '#334155',
                            fontWeight: 500,
                            whiteSpace: 'pre-line',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        {note.note}
                    </Typography>
                </Box>

                {/* BOTTOM ROW: Tagged Users & Context Label */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {note.tagged?.length > 0 ? (
                            <AvatarGroup max={4} sx={{
                                '& .MuiAvatar-root': {
                                    width: 26,
                                    height: 26,
                                    fontSize: '10px',
                                    border: '2px solid #fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }
                            }}>
                                {note.tagged.map((id) => {
                                    const att = attendees.find(a => a.id === id);
                                    if (!att) return null;
                                    const name = formatName(att.nickname || att.fname);
                                    return (
                                        <Tooltip key={id} title={name}>
                                            <Avatar src={att.avatar?.preview} {...(!att.avatar?.preview ? stringAvatar(name) : {})} />
                                        </Tooltip>
                                    );
                                })}
                            </AvatarGroup>
                        ) : (
                            <Typography variant="caption" sx={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: '10px' }}>
                                No tags
                            </Typography>
                        )}
                    </Box>

                    {/* Context Badge (Date or Day) */}
                    <Box sx={{
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '8px',
                        bgcolor: '#f1f5f9',
                        border: '1px solid #e2e8f0'
                    }}>
                        <Typography sx={{ fontSize: '9px', fontWeight: 800, color: '#64748b' }}>
                            {note.date ? note.date : note.day?.toUpperCase()}
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Paper>
    );
}



export default NoteCard;